import React, { useState } from 'react';
import {
  ChevronDown,
  Download,
  Phone,
  Mail,
  MapPin,
  FileText,
  Info,
} from 'lucide-react';

interface File {
  name: string;
  url: string;
  description: string;
}

const downloadFiles: File[] = [
  {
    name: 'TAT Schedule',
    url: '/files/TAT.pdf',
    description: 'Expected delivery timelines across different zones.',
  },
  {
    name: 'Lost & Damage Policy',
    url: '/files/lost-damage.pdf',
    description: 'Terms and conditions for shipment insurance and claims.',
  },
  {
    name: 'Standard Operating Procedure',
    url: '/files/standard.pdf',
    description: 'Guidelines for packaging and handing over shipments.',
  },
  {
    name: 'Shopify Integration Guide',
    url: '/files/integrate-shopify.pdf',
    description: 'Step-by-step instructions to connect your Shopify store.',
  },
  {
    name: 'WooCommerce Plugin',
    url: '/files/integrate-woocommerce.pdf',
    description: 'Documentation for WordPress/WooCommerce setup.',
  },
  {
    name: 'API Documentation',
    url: '/files/api-docs.pdf',
    description: 'Technical specifications for custom API integrations.',
  },
  {
    name: 'Prohibited Items List',
    url: '/files/prohibited.pdf',
    description: 'Items that are restricted from being shipped.',
  },
  {
    name: 'KYC Document Checklist',
    url: '/files/kyc.pdf',
    description: 'Required documents for merchant onboarding.',
  },
  {
    name: 'Return Policy',
    url: '/files/returns.pdf',
    description: 'Process and charges for RTO (Return to Origin) shipments.',
  },
  {
    name: 'Label Printing Guide',
    url: '/files/labels.pdf',
    description: 'Best practices for thermal and laser label printing.',
  },
  {
    name: 'Label Printing Guide',
    url: '/files/labels.pdf',
    description: 'Best practices for thermal and laser label printing.',
  },
  {
    name: 'TAT Schedule',
    url: '/files/TAT.pdf',
    description: 'Expected delivery timelines across different zones.',
  },
  {
    name: 'Lost & Damage Policy',
    url: '/files/lost-damage.pdf',
    description: 'Terms and conditions for shipment insurance and claims.',
  },
  {
    name: 'Standard Operating Procedure',
    url: '/files/standard.pdf',
    description: 'Guidelines for packaging and handing over shipments.',
  },
  {
    name: 'Shopify Integration Guide',
    url: '/files/integrate-shopify.pdf',
    description: 'Step-by-step instructions to connect your Shopify store.',
  },
  {
    name: 'WooCommerce Plugin',
    url: '/files/integrate-woocommerce.pdf',
    description: 'Documentation for WordPress/WooCommerce setup.',
  },
  {
    name: 'API Documentation',
    url: '/files/api-docs.pdf',
    description: 'Technical specifications for custom API integrations.',
  },
  {
    name: 'Prohibited Items List',
    url: '/files/prohibited.pdf',
    description: 'Items that are restricted from being shipped.',
  },
];

export default function SupportPage() {
  const [expandedFile, setExpandedFile] = useState<string | null>(null);

  const handleDownload = (file: File) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen px-4 lg:px-6 py-6 lg:py-8">
      <div className="mx-auto space-y-8">
        {/* Header */}
        <div className="space-y-1 mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-text">Support</h1>
          <p className="text-sm text-muted-foreground">
            Get in touch with us and find all the resources you need
          </p>
        </div>

        {/* Contact Box Section */}
        <div className="bg-sidebar rounded-lg p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Ring us up */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Phone className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-50">Ring us up</h3>
                </div>
              </div>
              <p className="text-gray-100 font-semibold text-lg">
                +91-7498088880
              </p>
            </div>

            {/* Write to us */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-50">
                    Write to us
                  </h3>
                </div>
              </div>
              <p className="text-gray-100 font-semibold underline cursor-pointer hover:text-text2 transition">
                support@parcelpicks.in
              </p>
            </div>

            {/* Visit us */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-gray-50">Visit us</h3>
                </div>
              </div>
              <p className="text-gray-100 text-sm leading-relaxed">
                Trimurti Sadan, 6X34+WWJ, Mahatma Phule Nagar Rd, Sawarkar
                Nagar, Mahatma Phule Nagar, Thane West, Thane, Maharashtra
                400606
              </p>
            </div>
          </div>
        </div>

        {/* Split Section: Resources (Left 40%) and Map (Right 60%) */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 items-start">
          {/* Left Side: Resources (Scrollable area) - Takes 4/10 columns */}
          <div className="lg:col-span-4 space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-text mb-2 flex items-center gap-2">
                <FileText className="w-6 h-6 text-teal-600" />
                Resources & Downloads
              </h2>
              <p className="text-muted-foreground text-sm">
                Documentation and guides for your logistics needs
              </p>
            </div>

            <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
              {downloadFiles.map((file) => (
                <div
                  key={file.name}
                  className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() =>
                      setExpandedFile(
                        expandedFile === file.name ? null : file.name
                      )
                    }
                    className="w-full px-5 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <ChevronDown
                        className={`w-4 h-4 text-teal-600 transition-transform ${
                          expandedFile === file.name ? 'rotate-180' : ''
                        }`}
                      />
                      <span className="text-md font-semibold text-text">
                        {file.name}
                      </span>
                    </span>
                  </button>

                  {expandedFile === file.name && (
                    <div className="px-5 pb-4 bg-muted/20 border-t border-border space-y-3">
                      <div className="pt-3">
                        <p className="text-xs text-muted-foreground  font-bold tracking-wider mb-1">
                          Description
                        </p>
                        <p className="text-sm text-text mb-4">
                          {file.description}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDownload(file)}
                        className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors w-full justify-center text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Quick Info Tip */}
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg flex gap-3">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-700 leading-relaxed">
                Can't find a specific document? Our technical team updates these
                resources weekly. Check back later or contact support.
              </p>
            </div>
          </div>

          {/* Right Side: Map (Fixed/Sticky) - Takes 6/10 columns */}
          <div className="lg:col-span-6 lg:sticky lg:top-8">
            <div className="space-y-8">
              <div className="rounded-lg overflow-hidden shadow-lg border border-border bg-card">
                <div className="p-4 border-b border-border bg-card">
                  <h3 className="font-bold text-text">Our Location</h3>
                  <p className="text-xs text-muted-foreground">
                    Find us at our headquarters
                  </p>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3767.7766310212355!2d72.95478437520862!3d19.20495608202639!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTnCsDEyJzE3LjgiTiA3MsKwNTcnMjYuNSJF!5e0!3m2!1sen!2sin!4v1755082976154!5m2!1sen!2sin"
                  width="100%"
                  height="450"
                  className="w-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Still need help section integrated here for better UI flow */}
              <div className="bg-white  rounded-lg p-6 border border-border shadow-sm">
                <h3 className="text-lg font-bold text-text mb-2">
                  Still need help?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  If you can't find what you're looking for, feel free to reach
                  out to our support team directly.
                </p>
                <div className="flex flex-col lg:flex-row gap-3">
                  <a
                    href="mailto:support@parcelpicks.in"
                    className="flex-1 flex items-center gap-2 bg-teal hover:bg-teal-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors justify-center"
                  >
                    <Mail className="w-4 h-4" />
                    Email Support
                  </a>
                  <a
                    href="tel:+917498088880"
                    className="flex-1 flex items-center gap-2 border border-teal text-teal hover:bg-teal-50 font-semibold py-2.5 px-4 rounded-lg transition-colors justify-center"
                  >
                    <Phone className="w-4 h-4" />
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
